<script lang="ts">
  import { onMount } from 'svelte'
  import DropdownMenu from './DropdownMenu.svelte'
  import DropdownMenuInsights from './DropdownMenuInsights.svelte'
  import Barchart from './Barchart.svelte'
  import { usersAnswers } from '../api/api'
  import { QUESTIONS_DEF } from '../constants/questions_def'
  import { mapValues, omit, groupBy, uniq } from 'lodash-es'
  import BubbleChart from './BubbleChart.svelte'

  let rawDataset = []
  let selectedSection = 'Profilazione'

  let filterResidenceMunicipality = null
  let filterStartMunicipality = null
  let filterEndMunicipality = null

  onMount(async () => {
    const { data } = await usersAnswers([0, 1000000])
    rawDataset = data

    // Retro-compatibility fix. Should be removable after going to prod
    rawDataset.forEach((d) => {
      if (d.answers.means_of_transport) {
        d.answers.used_means_of_transport_weekend = d.answers.means_of_transport
        delete d.answers.means_of_transport
      }
    })
  })

  $: insightsDataset = rawDataset.map((d) =>
    mapValues(
      omit(d.answers, ['start_survey', 'end_of_survey', 'MID_VIZ']),
      (answers, questionId) => {
        const question = QUESTIONS_DEF.find(({ id }) => id === questionId)
        if (question.maxAnswersNumber === 1 || question.maxAnswersNumber === null) {
          return answers[0].text
        } else {
          return answers.map((answer) => answer.text)
        }
      }
    )
  )

  $: residenceMunicipalities = uniq(insightsDataset.map((d) => d.municipality_of_residence)).sort()
  $: startMunicipalities = uniq(insightsDataset.map((d) => d.start_municipality)).sort()
  $: endMunicipalities = uniq(insightsDataset.map((d) => d.end_municipality)).sort()

  $: insightsDatasetFilteredResidence =
    filterResidenceMunicipality !== null
      ? insightsDataset.filter((a) => a.start_municipality === filterResidenceMunicipality)
      : insightsDataset
  $: insightsDatasetFilteredStartEnd = insightsDataset.filter(
    (a) =>
      (a.start_municipality === filterStartMunicipality || filterStartMunicipality === null) &&
      (a.end_municipality === filterEndMunicipality || filterEndMunicipality === null)
  )

  function groupAndCountBy(arr, col, grouper = null) {
    return Object.entries(groupBy(arr, col)).map(([label, items]) => ({
      label,
      value: items.length,
      items,
      group: grouper ? grouper(items) : undefined,
    }))
  }

  function groupForStackedBarchart(dataset, { level1, level2 = null }) {
    return groupAndCountBy(dataset, level1, level2 ? (g) => groupAndCountBy(g, level2) : null)
  }

  function distanceBins(d) {
    return d.mean_distance <= 10
      ? '10km'
      : d.mean_distance <= 20
      ? '20km'
      : d.mean_distance <= 40
      ? '40km'
      : d.mean_distance <= 80
      ? '80km'
      : d.mean_distance <= 100
      ? '100km'
      : '> 100km'
  }
</script>

<!--
  COLUMNS:

  municipality_of_residence
  gender
  age
  used_means_of_transport_weekend (was: means_of_transport)
  start_municipality
  end_municipality
  travel_reason
  how_many_travels
  travel_duration
  mean_distance
  used_means_of_transport
  reason_means_of_transport_car
  reason_means_of_transport_train_or_bus
  reason_means_of_transport_bike_or_walk
  reason_means_of_transport_carpooling
  reason_means_of_transport_auto_as_a_passenger
  reason_means_of_transport_auto_park_and_ride
  willing_to_avoid_auto_or_scooter
  willing_to_use_alternative_means_of_transport
  conditions_necessary_for_public_transport
  conditions_necessary_for_bike
  conditions_necessary_for_walk
  conditions_necessary_for_carpooling
  suggestions
-->

<DropdownMenu selected="Analisi delle risposte" />

<div class="insights-wrapper">
  <DropdownMenuInsights bind:selected={selectedSection} />

  {#if selectedSection === 'Profilazione'}
    <div class="section">
      <div class="title">Abitanti di un comune per fascia di età e numero di spostamenti</div>
      <Barchart
        stacked
        dataset={groupForStackedBarchart(insightsDatasetFilteredResidence, {
          level1: 'age',
          level2: 'gender',
        })}
      />
    </div>

    <div class="section">
      <div class="title">Abitanti di un comune in partenza</div>
      <Barchart
        dataset={groupForStackedBarchart(
          filterResidenceMunicipality
            ? insightsDataset.filter(
                (a) => a.municipality_of_residence === filterResidenceMunicipality
              )
            : insightsDataset,
          { level1: 'start_municipality' }
        )}
      />
    </div>

    <div class="section">
      <div class="title">Abitanti di un comune verso una destinazione</div>
      <Barchart
        dataset={groupForStackedBarchart(
          filterResidenceMunicipality
            ? insightsDataset.filter(
                (a) => a.municipality_of_residence === filterResidenceMunicipality
              )
            : insightsDataset,
          { level1: 'end_municipality' }
        )}
      />
    </div>

    <div class="section">
      <div class="title">Top 5 partenze</div>
      <Barchart
        dataset={groupForStackedBarchart(insightsDataset, {
          level1: 'start_municipality',
        })}
        limit={5}
      />
    </div>

    <div class="section">
      <div class="title">Top 5 destinazioni</div>
      <Barchart
        dataset={groupForStackedBarchart(insightsDataset, {
          level1: 'end_municipality',
        })}
        limit={5}
      />
    </div>
  {:else if selectedSection === 'Durata dello spostamento'}
    <div class="section">
      <div class="title">
        Abitanti di un comune verso una destinazione - durata dello spostamento
      </div>
      <Barchart
        dataset={groupForStackedBarchart(insightsDatasetFilteredStartEnd, {
          level1: 'travel_duration',
        })}
      />
    </div>

    <div class="section">
      <div class="title">
        Abitanti di un comune verso una destinazione - durata e distanza dello spostamento
      </div>
      <BubbleChart dataset={insightsDatasetFilteredStartEnd} groupByCategory="travel_duration" />
    </div>
  {:else if selectedSection === 'Mezzo di trasporto'}
    <div class="section">
      <div class="title">
        Abitanti di un comune verso una destinazione - mezzo di trasporto utilizzato
      </div>
      <Barchart
        dataset={groupForStackedBarchart(insightsDatasetFilteredStartEnd, {
          level1: 'used_means_of_transport',
        })}
      />
    </div>

    <div class="section">
      <div class="title">Durata dello spostamento</div>
      <Barchart
        stacked
        dataset={groupForStackedBarchart(insightsDatasetFilteredStartEnd, {
          level1: 'used_means_of_transport',
          level2: 'travel_duration',
        })}
      />
    </div>

    <div class="section">
      <div class="title">Distanza dello spostamento</div>
      <BubbleChart
        dataset={insightsDatasetFilteredStartEnd}
        groupByCategory={'used_means_of_transport'}
      />
    </div>
  {:else if selectedSection === "Motivi d'uso auto"}
    <div class="section">
      <div class="title">Abitanti di un comune verso una destinazione - motivo uso auto</div>
      <Barchart
        stacked
        dataset={groupForStackedBarchart(
          insightsDatasetFilteredStartEnd
            .filter((a) => a.reason_means_of_transport_car !== undefined)
            .flatMap((a) =>
              a.reason_means_of_transport_car.map((r) => ({
                reason_means_of_transport_car: r,
                age: a.age,
              }))
            ),
          { level1: 'reason_means_of_transport_car', level2: 'age' }
        )}
      />
    </div>
  {:else if selectedSection === 'Propensione al cambiamento'}
    <div class="section">
      <div class="title">
        Distanza/durata percorso - propensione a modi alternativi all’ uso auto
      </div>
      <BubbleChart
        dataset={insightsDatasetFilteredStartEnd}
        groupByCategory={'willing_to_use_alternative_means_of_transport'}
      />
    </div>

    <div class="section">
      <div class="title">
        Abitanti di un comune verso una destinazione - propensione a modi alternativi all'auto
      </div>
      <Barchart
        stacked
        dataset={groupForStackedBarchart(
          insightsDatasetFilteredStartEnd.filter(
            (a) => a.willing_to_use_alternative_means_of_transport !== undefined
          ),
          { level1: 'willing_to_use_alternative_means_of_transport', level2: 'age' }
        )}
      />
    </div>

    <div class="section">
      <div class="title">
        Abitanti di un comune verso una destinazione - propensione a modi alternativi all'auto
      </div>
      <Barchart
        stacked
        dataset={groupForStackedBarchart(
          insightsDatasetFilteredStartEnd.filter(
            (a) => a.willing_to_use_alternative_means_of_transport !== undefined
          ),
          { level1: 'willing_to_use_alternative_means_of_transport', level2: 'age' }
        )}
      />
    </div>

    {#each [{ title: 'Propensione al carpooling per fascia di età e durata spostamento', answerText: 'Si, potrei prevedere di fare carpooling' }, { title: 'Propensione al trasporto pubblico per fascia di età e durata spostamento', answerText: 'Si, potrei prevedere di usare il trasporto pubblico' }, { title: 'Propensione alla bicicletta per fascia di età e durata spostamento', answerText: 'Si, potrei prevedere di usare la bicicletta e/o la bicicletta elettrica' }, { title: 'Propensione al park & ride + traporto pubblico per fascia di età e durata spostamento', answerText: 'Si, potrei prevedere di fare un tratto del mio viaggio in trasporto pubblico usando un park & ride sul percorso' }, { title: "Propensione all'andare a piedi per fascia di età e durata spostamento", answerText: 'Si, potrei prevedere di andare a piedi' }] as { title, answerText }}
      <div class="section">
        <div class="title">
          {title}
        </div>
        <Barchart
          stacked
          dataset={groupForStackedBarchart(
            insightsDatasetFilteredStartEnd.filter(
              (a) => a.willing_to_use_alternative_means_of_transport === answerText
            ),
            { level1: 'age', level2: 'travel_duration' }
          )}
        />
      </div>
    {/each}
  {:else}
    BUG
  {/if}

  <div class="filters-container">
    <div class="filters">
      {#if selectedSection === 'Profilazione'}
        <select bind:value={filterResidenceMunicipality}>
          <option selected value={null}>Comune di residenza: Tutti</option>
          {#each residenceMunicipalities as m}
            <option>{m}</option>
          {/each}
        </select>
      {:else}
        <select bind:value={filterStartMunicipality}>
          <option selected value={null}>Partenza: Tutti</option>
          {#each startMunicipalities as m}
            <option>{m}</option>
          {/each}
        </select>
        <select bind:value={filterEndMunicipality}>
          <option selected value={null}>Arrivo: Tutti</option>
          {#each endMunicipalities as m}
            <option>{m}</option>
          {/each}
        </select>
      {/if}
    </div>
  </div>
</div>

<style>
  .insights-wrapper {
    width: 90%;
    max-width: 700px;
    padding-top: 90px;
    padding-left: 1em;
    padding-right: 1em;
  }

  .section {
    margin: 60px 0;
  }

  .title {
    font-size: 16px;
    font-weight: 600;
  }

  .filters-container {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #1e1e1e;
    display: flex;
    justify-content: center;
  }
  .filters-container .filters {
    width: 90%;
    max-width: 700px;
    display: flex;
    padding: 10px;
    gap: 10px;
  }
  .filters-container select {
    flex: 1 0 0;
    min-width: 0;
    height: 40px;
    margin-bottom: 0;
  }

  select {
    color: white;
    background: black;
    border: 2px solid white;
  }
</style>
